// LocatPage.tsx
import React, {FC, useCallback, useEffect, useState} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {
  IGeneralTariffModel,
  initialGeneralTariff,
  validationGeneralTariff,
  RELATED_OFFICE,
  TARIFF_GROUPS,
} from './GeneralTariffModel'
import {
  createGeneralTariff,
  deleteGeneralTariff,
  loadGeneralTariffs,
  updateGeneralTariff,
} from './Api'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import CheckboxInput from '../../../../shared/components/CheckboxInput'
import {GeneralTariffTable} from './GeneralTariffTable'
import {loadBranches} from '../branch/Api'
import {IBranchModel} from '../branch/BranchModel'
import { branches, reservationSources } from '../../../../shared/models/SharedApiLookups'

const GeneralTariffPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [generalTariffs, setGeneralTariffs] = useState<IGeneralTariffModel[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<string | 'form'>('form')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0) 
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [error, setError] = useState<string | null>(null)
  const [showActiveOnly, setShowActiveOnly] = useState(false) // Aktif filtre checkbox
  const existingTariffs = generalTariffs // state veya API’den gelen liste
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [reservationSourcesOptions, setReservationSourcesOptions] = useState<{value: string; label: string}[]>([])

    useEffect(() => {
      Promise.all([branches(), reservationSources()]).then(
        ([brn, rs]) => {  
          setBranchOptions(brn)
          setReservationSourcesOptions(rs) 
        }
      )
    }, [])

  const tabOptions: TabOption<'form' | 'list'>[] = [
    {key: 'form', title: 'Genel Tarife Tanımlama', icon: 'bi bi-geo-alt'},
    {key: 'list', title: 'Genel Tarife Listesi', icon: 'bi bi-list'},
  ]
  const fetchGeneralTariffs = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)

      try {
        const response = await loadGeneralTariffs(page, 5, sort, order)
        if (response.data.hasError) {
          throw new Error(response.data.message || 'Failed to fetch branches')
        }
        setGeneralTariffs(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred')
      } finally {
        setLoading(false)
      }
    },
    []
  )
 

  // Genel tarifeleri sadece tab "list" olduğunda çek
  useEffect(() => {
    if (tab === 'list') {
      fetchGeneralTariffs(0)
    }
  }, [tab, fetchGeneralTariffs])

  const handleSave = async (values: IGeneralTariffModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateGeneralTariff({...values, id: selectedId})
        showSuccessToast('Genel Tarife başarıyla güncellendi')
      } else {
        await createGeneralTariff(values)
        showSuccessToast('Genel Tarife başarıyla oluşturuldu')
      }

      setCurrentPage(0)
      await fetchGeneralTariffs(0)

      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback(
    (genTar: IGeneralTariffModel, formik: FormikProps<IGeneralTariffModel>) => {
      formik.setValues(genTar)
      setSelectedId(genTar.id)
      setTab('form')
    },
    []
  )

  const handleDeleteClick = useCallback((genTar: IGeneralTariffModel) => {
    setSelectedId(genTar.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteGeneralTariff(selectedId)
        showSuccessToast('Genel Tarife başarıyla silindi')
        await fetchGeneralTariffs(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Genel Tarife silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchGeneralTariffs(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchGeneralTariffs]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchGeneralTariffs(currentPage, column, order)
    },
    [currentPage, fetchGeneralTariffs]
  )

  const pageSize = 5 // sayfa başına gösterilen veri sayısı
  // Aktif filtreleme ve global index ekleme
  const filteredData = (
    showActiveOnly ? generalTariffs.filter((t) => !t.cancellationDate) : generalTariffs
  ).map((item, idx) => ({
    ...item,
    _index: currentPage * pageSize + idx + 1,
  }))

  return (
    <>
      <Formik
        initialValues={initialGeneralTariff}
        validationSchema={validationGeneralTariff(existingTariffs)}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
            setTab('form')
          }

          return (
            <>
              <FormTabHeader
                formId='generalTariffForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />

              {tab === 'form' && (
                <div className='card mb-5'>
                  <Form id='generalTariffForm' className='form card-body'>
                    <div className='row mb-6'>
                      <TextInput name='rateName' label='Tarife Adı' wrapperClassName='col-md-6' />
                      <TextInput
                        name='actionDate'
                        label='İşlem Tarihi'
                        type='date'
                        wrapperClassName='col-md-6'
                      />
                    </div>

                    <div className='row mb-4'>
                      <div className='col-md-6'>
                        <h5>Gün Aralıkları</h5>
                        <div className='row fw-bold text-center mb-2'>
                          <div className='col-3'></div>
                          <div className='col-3 text-center'>Gün Aralığı</div>
                          <div className='col-3 text-center'>Km Sınırı</div>
                          <div className='col-3 text-center'>Aşım Bedeli</div>
                        </div>

                        <div className='row mb-2 align-items-center'>
                          <div className='col-3 fw-bold'>Gün 1</div>
                          <div className='col-3'>
                            <TextInput
                              name='dayOne.dayRange'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayOne.kmLimit'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayOne.overLimitFee'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                        </div>

                        <div className='row mb-2 align-items-center'>
                          <div className='col-3 fw-bold'>Gün 2</div>
                          <div className='col-3'>
                            <TextInput
                              name='dayTwo.dayRange'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayTwo.kmLimit'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayTwo.overLimitFee'
                              type='number'
                              label=''
                              placeholder='0'
                              className='form-control form-control-sm'
                            />
                          </div>
                        </div>

                        <div className='row mb-2 align-items-center'>
                          <div className='col-3 fw-bold'>Gün 3</div>
                          <div className='col-3'>
                            <TextInput
                              name='dayThree.dayRange'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayThree.kmLimit'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayThree.overLimitFee'
                              type='number'
                              label=''
                              placeholder='0'
                              className='form-control form-control-sm'
                            />
                          </div>
                        </div>

                        <div className='row mb-2 align-items-center'>
                          <div className='col-3 fw-bold'>Gün 4</div>
                          <div className='col-3'>
                            <TextInput
                              name='dayFour.dayRange'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayFour.kmLimit'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayFour.overLimitFee'
                              type='number'
                              label=''
                              placeholder='0'
                              className='form-control form-control-sm'
                            />
                          </div>
                        </div>

                        <div className='row mb-2 align-items-center'>
                          <div className='col-3 fw-bold'>Gün 5</div>
                          <div className='col-3'>
                            <TextInput
                              name='dayFive.dayRange'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayFive.kmLimit'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='dayFive.overLimitFee'
                              type='number'
                              label=''
                              placeholder='0'
                              className='form-control form-control-sm'
                            />
                          </div>
                        </div>

                        <div className='row mb-2 align-items-center'>
                          <div className='col-3 fw-bold'>Gün 6</div>
                          <div className='col-3'>
                            <TextInput
                              name='daySix.dayRange'
                              type='number'
                              label=''
                              placeholder='0'
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='daySix.kmLimit'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='daySix.overLimitFee'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                        </div>

                        <div className='row mb-2 align-items-center'>
                          <div className='col-3 fw-bold'>Gün 7</div>
                          <div className='col-3'>
                            <TextInput
                              name='daySeven.dayRange'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='daySeven.kmLimit'
                              type='number'
                              label=''
                              className='form-control form-control-sm'
                            />
                          </div>
                          <div className='col-3'>
                            <TextInput
                              name='daySeven.overLimitFee'
                              type='number'
                              label=''
                              placeholder='0'
                              className='form-control form-control-sm'
                            />
                          </div>
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <TextInput
                          name='cancellationDate'
                          label='İptal Tarihi'
                          type='date'
                          wrapperClassName='mb-2'
                        />
                        <TextInput
                          name='discount'
                          label='İndirim'
                          type='number'
                          wrapperClassName='mb-2'
                        />
                        <SelectInput
                          name='rateGroup'
                          label='Tarife Grubu'
                          wrapperClassName='mb-2'
                          options={TARIFF_GROUPS}
                        />
                        <SelectInput
                          name='actionBranch'
                          label='İşlem Şube'
                          wrapperClassName='mb-2'
                          options={branchOptions}
                        />

                        <TextInput
                          name='startDate'
                          type='date'
                          label='Başlangıç Tarihi'
                          wrapperClassName='mb-2'
                        />
                        <TextInput
                          name='endDate'
                          type='date'
                          label='Bitiş Tarihi'
                          wrapperClassName='mb-2'
                        />

                        <div className='row mb-3'>
                          <div className='col-md-6 mb-2'>
                            <CheckboxInput name='scdwInsurance' label='Sigorta SCDW' />
                          </div>
                          <div className='col-md-6'>
                            <CheckboxInput name='miniDamage' label='Mini Hasar' />
                          </div>
                          <div className='col-md-6 mb-2'>
                            <CheckboxInput name='theftIncluded' label='Hırsızlık Dahil' />
                          </div>
                          <div className='col-md-6'>
                            <CheckboxInput name='scdwIncluded' label='SCDW Zorunlu' />
                          </div>
                          <div className='col-md-6'>
                            <CheckboxInput name='display' label='Gösterme' />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Form>
                </div>
              )}
              {tab === 'list' && (
                <div className='card'>
                  <div className='card-body pt-0'>
                    <div
                      className='mb-4'
                      style={{display: 'flex', alignItems: 'center', gap: '10px'}}
                    >
                      <input
                        type='checkbox'
                        id='activeOnlyCheckbox'
                        checked={showActiveOnly}
                        onChange={(e) => setShowActiveOnly(e.target.checked)}
                        style={{
                          width: '25px',
                          height: '25px',
                          cursor: 'pointer',
                        }}
                      />
                      <label
                        htmlFor='activeOnlyCheckbox'
                        style={{fontSize: '1.2rem', fontWeight: 'bold', cursor: 'pointer'}}
                      >
                        Sadece Aktifleri Göster
                      </label>
                    </div>

                    <GeneralTariffTable
                      data={filteredData}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(generalTariff) => handleEdit(generalTariff, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu Genel Tarifeyi silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export {GeneralTariffPage}
